// ===========================================
// Email Preview Dialog Component
// ===========================================
// Full email preview dialog with rendered HTML

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  IconButton,
  Paper,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DOMPurify from 'dompurify';
import { useQuery } from '@tanstack/react-query';
import { getFullEmailPreview } from '../../../api/emailBranding.api';
import SendTemplateTestButton from './SendTemplateTestButton';

interface EmailPreviewDialogProps {
  open: boolean;
  templateKey: string;
  onClose: () => void;
  /** Hides Send test email (it needs communications_settings.write). */
  readOnly?: boolean;
}

export default function EmailPreviewDialog({
  open,
  templateKey,
  onClose,
  readOnly = false,
}: EmailPreviewDialogProps) {
  // Fetch full email preview
  const {
    data: preview,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['fullEmailPreview', templateKey],
    queryFn: () => getFullEmailPreview(templateKey),
    enabled: open && !!templateKey,
  });

  if (!open) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h6">
              {isLoading ? 'Loading Preview...' : preview?.templateName || 'Email Preview'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Preview with sample data
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {isLoading && (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error">Failed to load email preview</Alert>
        )}

        {preview && (
          <Box>
            {/* Subject Line */}
            <Box mb={2}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Subject
              </Typography>
              <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'background.default' }}>
                <Typography variant="body1" fontWeight={500}>
                  {preview.subject}
                </Typography>
              </Paper>
            </Box>

            {/* Preheader */}
            <Box mb={2}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Preheader (Preview Text)
              </Typography>
              <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'background.default' }}>
                <Typography variant="body2" color="text.secondary">
                  {preview.preheader}
                </Typography>
              </Paper>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Full Email HTML */}
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Email Body
              </Typography>
              <Paper
                variant="outlined"
                sx={{
                  p: 0,
                  bgcolor: 'background.paper',
                  maxHeight: 500,
                  overflow: 'auto',
                }}
              >
                <Box
                  sx={{
                    '& a': { color: 'primary.main' },
                    '& img': { maxWidth: '100%', height: 'auto' },
                  }}
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(preview.html) }}
                />
              </Paper>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ flexWrap: 'wrap', rowGap: 1 }}>
        <Box sx={{ mr: 'auto' }}>
          {!readOnly && <SendTemplateTestButton templateKey={templateKey} disabled={!preview} />}
        </Box>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
